import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { ButtonLink, SectionLabel } from "@/components/common";
import { useFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const Detail = ({ o }) => (
  <motion.div
    key={o.key}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    data-testid="build-option-detail"
    data-option={o.key}
  >
    <p className="label-mono"><span className="text-gold">{o.number}</span> — {o.label}</p>
    <h3 className="mt-5 font-display text-3xl leading-[1.05] md:text-5xl" data-testid="build-option-headline">{o.headline}</h3>
    <p className="mt-6 text-base leading-relaxed text-silver md:text-lg">{o.description}</p>
    <div className="mt-10 grid gap-10 md:grid-cols-2">
      <div>
        <p className="label-mono mb-4 text-turq">How I'd approach it</p>
        <ol className="space-y-3">
          {o.approach.map((a, i) => (
            <li key={a} className="flex gap-4 text-sm leading-relaxed"><span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>{a}</li>
          ))}
        </ol>
      </div>
      <div>
        <p className="label-mono mb-4 text-turq">What you get</p>
        <ul className="space-y-3">
          {o.deliverables.map((d) => (
            <li key={d} className="flex items-start gap-3 text-sm leading-relaxed"><Check className="mt-0.5 h-4 w-4 shrink-0 text-turq" aria-hidden />{d}</li>
          ))}
        </ul>
      </div>
    </div>
    <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-line/10 pt-8">
      <ButtonLink to={`/contact?type=${o.contact_type}&project=${o.key}`} testId="build-option-cta">Start this project</ButtonLink>
      <span className="label-mono">Typical timeline · <span className="text-fg">{o.timeline}</span></span>
    </div>
  </motion.div>
);

export const BuildSelector = () => {
  const { data } = useFetch("/build-options");
  const [selected, setSelected] = useState(null);
  const options = data || [];
  const current = options.find((o) => o.key === selected);
  return (
    <section id="what-can-we-build" data-testid="build-selector-section" className="border-y border-line/10 bg-surface py-24 md:py-36">
      <div className="container-x">
        <SectionLabel number="05">Interactive</SectionLabel>
        <h2 className="mt-6 font-display text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-8xl">What can <em className="text-turq">we</em> build?</h2>
        <p className="mt-6 max-w-xl text-base text-silver md:text-lg">Pick the closest thing to what you have in mind. I'll show you how I'd approach it.</p>
        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5" role="group" aria-label="What would you like to build?">
            <ul className="border-t border-line/15">
              {options.map((o) => {
                const active = o.key === selected;
                return (
                  <li key={o.key} className="border-b border-line/15">
                    <button
                      type="button"
                      aria-pressed={active}
                      onClick={() => setSelected(o.key)}
                      data-testid={`build-option-${o.key}`}
                      className={cn("group flex w-full items-baseline gap-5 py-4 text-left transition-[color,padding] duration-300 md:py-5", active ? "pl-3 text-turq" : "text-fg hover:pl-2 hover:text-turq")}
                    >
                      <span className={cn("font-mono text-xs", active ? "text-turq" : "text-gold")}>{o.number}</span>
                      <span className="font-display text-2xl leading-tight md:text-3xl">{o.label}</span>
                      {active && <motion.span layoutId="build-dot" className="ml-auto h-2 w-2 self-center rounded-full bg-turq" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="lg:col-span-7" aria-live="polite">
            <div className="rounded-lg border border-line/10 bg-canvas p-7 md:p-12 lg:sticky lg:top-28">
              <AnimatePresence mode="wait">
                {current ? (
                  <Detail o={current} />
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex min-h-[380px] flex-col justify-between" data-testid="build-option-placeholder">
                    <span className="font-display text-[9rem] leading-none text-gold/70">?</span>
                    <p className="max-w-sm font-display text-3xl leading-tight">Select an option to see the approach, the deliverables and the first step.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
